import Button from "@/components/ui/Button";
import React, { useState } from "react";
import useGlobalStore from "@/store/globalStore";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useShallow } from "zustand/react/shallow";
import useMediaUserOnboardingStore from "../store/mediaUserOnboardingStore";

interface Props {
  firstName: string;
  agencyName?: string;
}

const ExistingUserWelcome: React.FC<Props> = ({ firstName, agencyName }) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { acceptExistingUser, inviteData } = useMediaUserOnboardingStore(
    useShallow((state) => ({
      acceptExistingUser: state.acceptExistingUser,
      inviteData: state.inviteData,
    }))
  );
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const setError = useGlobalStore((state) => state.setError);

  const handleAccept = async () => {
    setIsLoading(true);
    try {
      const token = searchParams.get("token");
      if (!token) {
        setError("Invalid invitation token");
        return;
      }

      const result = await acceptExistingUser(token);
      
      // Redirect to login with email and agency_id pre-filled
      navigate(`/login?email=${encodeURIComponent(result.email)}&from_invite=true&agency_id=${encodeURIComponent(result.agency_id)}`);
    } catch (err: any) {
      setIsLoading(false);
      setError(err?.message || "Failed to accept invitation.");
    }
  };

  return (
    <div className="bg-[#F8F8F8] flex flex-col items-center justify-center px-4 py-14 min-h-[calc(100vh-3rem)]">
      <div className="max-w-2xl w-full bg-white rounded-lg shadow-lg p-12 text-center">
        <h1 className="text-5xl font-bold text-[#EA33C0] mb-6">MediaBridge</h1>
        
        <h2 className="text-3xl font-bold text-gray-800 mb-4">
          You're popular, {firstName}!
        </h2>
        
        <p className="text-lg text-gray-600 mb-2">
          You've been invited to collaborate with{" "}
          <strong>{agencyName || "an agency"}</strong>
        </p>
        <p className="text-lg text-gray-600 mb-8">on MediaBridge.</p>

        <div className="space-y-4">
          <Button
            type="primary"
            onClick={handleAccept}
            isLoading={isLoading}
            className="px-8 py-3 text-lg"
          >
            Accept Invite
          </Button>
          
          <p className="text-sm text-gray-500 mt-6">
            After accepting, you'll be redirected to sign in with your existing account.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ExistingUserWelcome;
